/**
 * Scoring for the "Today's Plan" view.
 *
 * One function that takes any FollowUpAccount from any bucket and
 * returns a single "should the rep see this today" score.  Higher =
 * surface sooner.  Pinned accounts get a giant boost so they always
 * land at the top; bucket priority + velocity signals + lifetime
 * volume drive the rest.  Recently-visited accounts (≤3 days) are
 * heavily suppressed so the rep isn't sent back to a buyer they
 * spoke with two days ago.
 *
 * Keeping this in its own module so it's easy to tune the weights
 * without touching UI code.
 */

import type { FollowUpAccount } from '../../api/depletions';
import { lacksCoreWhiskey, missingCoreWhiskey } from '../../lib/coreWhiskey';
import { parseBackendDate } from '../../util/dates';

// Core-whiskey gap dominates the normal signal range (base + boosts top
// out around ~450) but stays well below a pin (1,000,000), so accounts
// missing a Rye or Bourbon float to the top of the picks without
// overriding an explicit pin or the recent-visit cooldown.
const CORE_GAP_BOOST = 1000;

const BUCKET_BASE: Record<string, number> = {
  '0-30 days': 50, // Nurture
  '30-60 days': 220, // Check In (highest yield)
  '60-90 days': 170, // Re-Engage
  '90-120 days': 110, // Win Back
  '120+ days': 30, // At Risk
};

// Rep's home address fields used to rank "what's near me" first.
// Sourced from the rep profile (home_zip set in Account Settings).
export interface HomeContext {
  home_zip: string | null;
  home_state: string | null;
}

// US ZIP-5 exact == literal neighborhood.  ZIP-3 prefix == same
// sectional center / metro (336xx = Tampa, 322xx = Jacksonville,
// 328xx = Orlando, etc.).  Returns the strength of the match, or
// null if there's no overlap or we don't have both ZIPs.
export type Proximity = 'zip5' | 'zip3' | null;

export function nearHome(account: FollowUpAccount, home: HomeContext | null): Proximity {
  if (!home?.home_zip || !account.zip_code) return null;
  const homeZip = home.home_zip.replace(/\D/g, '').slice(0, 5);
  const acctZip = account.zip_code.replace(/\D/g, '').slice(0, 5);
  if (homeZip.length < 3 || acctZip.length < 3) return null;
  if (homeZip.length === 5 && homeZip === acctZip) return 'zip5';
  if (homeZip.slice(0, 3) === acctZip.slice(0, 3)) return 'zip3';
  return null;
}

export function todayScore(
  account: FollowUpAccount,
  bucketLabel: string,
  home?: HomeContext | null,
): number {
  if (account.is_pinned) return 1_000_000;

  // Recently visited (within ~3 days) — heavy suppress.
  if (account.last_visit_date) {
    const days = (Date.now() - parseBackendDate(account.last_visit_date).getTime()) / 86_400_000;
    if (days <= 3) return -1;
  }

  const base = BUCKET_BASE[bucketLabel] ?? 0;
  const velocity = account.velocity_pct;
  // Thresholds match the Follow-Up Tracker momentum chip (±10%) so the
  // Growing / Declining a rep sees on a row is the same signal that
  // boosts it into Today's Plan.
  const decliningBoost = velocity !== null && velocity <= -10 ? 60 : 0;
  const growingBoost = velocity !== null && velocity >= 10 ? 30 : 0;
  // velocity_pct is null when prior_3m was zero — that's either a
  // brand-new account or a dormant account placing first orders
  // again.  Either way it's worth a touch.
  const newOrReturningBoost = velocity === null ? 50 : 0;

  const lifetime = Number(account.total_9l) || 0;
  const lifetimeBoost = lifetime > 0 ? Math.log10(lifetime + 1) * 12 : 0;

  // Proximity boost — calibrated so a ZIP-3 metro match outweighs
  // a non-local lifetime-volume edge but a sharply declining
  // non-local account can still break into the picks.  Falls to
  // zero if the rep has no home ZIP set or the account is missing
  // a ZIP — behavior is unchanged from before.
  const prox = home ? nearHome(account, home) : null;
  const proximityBoost = prox === 'zip5' ? 100 : prox === 'zip3' ? 60 : 0;

  // Core-whiskey gap — account doesn't carry both a Rye and a Bourbon.
  // HY wants these pushed to the top so the core line gets pitched.
  const coreGapBoost = lacksCoreWhiskey(account.products) ? CORE_GAP_BOOST : 0;

  return (
    base +
    decliningBoost +
    growingBoost +
    newOrReturningBoost +
    lifetimeBoost +
    proximityBoost +
    coreGapBoost
  );
}

/** How many accounts land in Today's Plan by default. */
export const TODAY_PLAN_LIMIT = 20;

// =====================================================================
// "Why picked" rationale shown next to each Today's Plan row
// =====================================================================
//
// Returns the dominant signal that drove this account into the
// curated list, in priority order:
//
//   1. Pinned — the rep explicitly flagged this
//   2. Declining — velocity dropped > 20% (urgent)
//   3. New activity — first orders just landed (opportunity)
//   4. Otherwise — fall back to the bucket the account came from
//
// Returned alongside the matching brand-tone hex so the chip in the
// UI can colour-code at a glance.
export interface TodayReason {
  label: string;
  tone: string;
  detail: string;
}

export function getTodayReason(account: {
  is_pinned?: boolean;
  velocity_pct: number | null;
  days_since: number;
  products?: string[];
}): TodayReason {
  if (account.is_pinned) {
    return {
      label: 'Pinned by you',
      tone: '#bb8c3f',
      detail: 'You flagged this account for your next visit, so it always lands at the top.',
    };
  }
  // Core-whiskey gap outranks every automatic signal — HY wants these
  // pitched first. Only a pin sits above it.
  const coreMissing = missingCoreWhiskey(account.products);
  if (coreMissing.length > 0) {
    const gap = coreMissing.join(' or ');
    return {
      label: coreMissing.length === 2 ? 'No Rye or Bourbon' : `No ${gap}`,
      tone: '#9a3412',
      detail: `This account doesn't carry ${gap} — pitch the core whiskey line to close the gap.`,
    };
  }
  const velocity = account.velocity_pct;
  if (velocity !== null && velocity <= -10) {
    return {
      label: `Declining ${Math.round(Math.abs(velocity))}%`,
      tone: '#a13a3a',
      detail:
        'Recent 3-month volume is down 10%+ versus the prior 3 months — worth a touch before the slide continues.',
    };
  }
  if (velocity === null && account.days_since < 90) {
    return {
      label: 'New activity',
      tone: '#326eb8',
      detail:
        'First orders just landed (or a dormant account just resumed) — nurture the relationship while it’s fresh.',
    };
  }
  if (account.days_since < 30) {
    return {
      label: 'Active customer',
      tone: '#22865a',
      detail: 'Recently ordering — a light check-in keeps the relationship warm.',
    };
  }
  if (account.days_since < 60) {
    return {
      label: 'Sweet spot for re-order',
      tone: '#326eb8',
      detail:
        'Last shipment was 1–2 months ago — the highest-yield window for prompting a re-order with availability + a seasonal promo.',
    };
  }
  if (account.days_since < 90) {
    return {
      label: 'Re-engage call',
      tone: '#a86b1e',
      detail:
        'Last shipment was 2–3 months ago — schedule a real conversation to surface what’s stalling.',
    };
  }
  if (account.days_since < 120) {
    return {
      label: 'Win-back',
      tone: '#b25a1e',
      detail:
        'Slipping — last shipment was 3–4 months ago. Try a case deal or incentive to restart.',
    };
  }
  return {
    label: 'Rescue attempt',
    tone: '#a13a3a',
    detail: 'Dormant 4+ months — decide if recoverable, and act fast if so.',
  };
}
